import { TableOperationHeader } from '@getsynapse/design-system';
import ExportIcon from 'assets/icons/export.svg';
import FilterButton from 'Organisms/TableHeaderActionButtons/FilterButton';
import MoreActionsPopup, {
  OptionProps,
} from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import React, { useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch } from 'react-redux';
import AppliedFiltersTags from '../../../../../Organisms/AppliedFiltersTags/AppliedFiltersTags';
import { setBudgetFilters } from '../../../../../state/BudgetList/budgetListSlice';
import { BudgetFilters } from '../../../../../utils/constants/budget';
import { objKeyAsString } from '../../../../../utils/customTypes';
import useBudgetFilters from '../../../hooks/useBudgetFilters';
import BudgetFiltersSidePanel from '../components/BudgetSidePanelFilters/BudgetFiltersSidePanel';

const BudgetPlanTableHeader: React.FC<{
  selectedBudgetId?: string;
  appliedFilters: BudgetFilters;
  filterButtonEnabled?: Boolean;
  saveFilters: (updatedFilters: BudgetFilters) => void;
  exportCsvData: () => void;
  onDuplicateBudget: () => void;
  onRenameBudget: () => void;
}> = ({
  selectedBudgetId,
  appliedFilters,
  filterButtonEnabled = false,
  saveFilters,
  exportCsvData,
  onDuplicateBudget,
  onRenameBudget,
}) => {
  const { getOptionsByKey } = useBudgetFilters();
  const dispatch = useDispatch();
  const [shouldDisplayFilterComponent, setShouldDisplayFilterComponent] =
    useState<boolean>(false);

  const handleUpdateFilters = (updatedFilters: BudgetFilters) => {
    // Carry the budgetId on the live filter so the table's Cost of Labor
    // expansion can tell this budget's filter apart immediately, without
    // waiting on the saveFilters round-trip (which may lag or fail). budgetId
    // is an id string rather than a Filter value, so type it loosely — same as
    // the persisted filter settings and handleSaveFiltersSettings already do.
    const liveFilters: objKeyAsString = {
      ...updatedFilters,
      budgetId: selectedBudgetId,
    };
    dispatch(setBudgetFilters(liveFilters));
    saveFilters(updatedFilters);
  };
  const tableActionsList: Partial<OptionProps>[] = [
    {
      label: intl.get('EXPORT_DATA'),
      selectOption: () => {
        exportCsvData();
      },
      iconSrc: ExportIcon,
    },
    {
      label: intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.TITLE'),
      iconName: 'copy-outline',
      selectOption: () => {
        onDuplicateBudget();
      },
    },
    {
      label: intl.get('BUDGET_PAGE.RENAME_BUDGET_MODAL.TITLE'),
      iconName: 'pencil-outline',
      selectOption: () => {
        onRenameBudget();
      },
    },
  ];

  const toggleFilterComponent = () =>
    setShouldDisplayFilterComponent((prevState) => !prevState);

  return (
    <div className='px-px'>
      <BudgetFiltersSidePanel
        selectedBudgetId={selectedBudgetId}
        open={shouldDisplayFilterComponent || false}
        onClose={toggleFilterComponent}
        filters={appliedFilters}
        onUpdateFilters={handleUpdateFilters}
      />
      <TableOperationHeader
        className='border-b-0'
        searchInputContainerClassName='z-5'
        hideSearch={true}
        actionButtons={
          <>
            <FilterButton
              onToggleFilter={() => {
                setShouldDisplayFilterComponent((prevState) => !prevState);
              }}
              disabled={!filterButtonEnabled}
              aria-pressed={shouldDisplayFilterComponent}
              data-cy='projects-table__filter-button'
            />
            <MoreActionsPopup options={tableActionsList} />
          </>
        }
      />
      {selectedBudgetId &&
        selectedBudgetId === String(appliedFilters.budgetId) && (
          <AppliedFiltersTags<BudgetFilters>
            savedFilters={appliedFilters}
            onUpdateFilters={handleUpdateFilters}
            onDisplayAllFilters={toggleFilterComponent}
            getOptionsbyKey={getOptionsByKey}
            dataTestid='applied-budget-filters-tags'
          />
        )}
    </div>
  );
};

export default BudgetPlanTableHeader;
